import { Link } from '@tanstack/react-router';
import { describeRepeat, type ScheduledMessage } from '@avslack/shared';
import { channelTitle, useChannels } from '../data/channels';
import { useUsers } from '../data/users';
import { useCancelScheduled, useScheduled } from '../data/workspace';
import { useSession } from '../lib/session';
import { Dialog, useAnimatedClose } from './Dialog';
import { useConfirm } from './Confirm';
import { Icon } from './Icon';

/**
 * Az UTEMEZETT es ISMETLODO uzeneteim (versenyaudit U9).
 *
 * Eddig az oldalsav csak annyit mondott, hogy "2 ütemezett üzenet vár" - azt
 * nem, hogy melyik, hova, mikor, es lemondani sem lehetett. Egy ismetlodo
 * uzenetnel ez nem fer bele: aki heti bejelentest allit be, annak latnia kell,
 * es le kell tudnia allitani.
 */

const WHEN: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

export function ScheduledDialog({ onClose: closeNow }: { onClose: () => void }) {
  const onClose = useAnimatedClose(closeNow);
  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Ütemezett üzenetek"
      description="Ami még elmegy a nevedben: egyszeri és ismétlődő üzenetek."
    >
      <ScheduledList onNavigate={onClose} />
    </Dialog>
  );
}

/**
 * Az utemezett uzenetek LISTAJA - az ablak es a Piszkozatok oldal (U8) is ezt
 * mutatja. A `onNavigate` a csatornara ugraskor fut (az ablak itt zarodik).
 */
export function ScheduledList({ onNavigate }: { onNavigate?: () => void }) {
  const onClose = onNavigate ?? (() => undefined);
  const { data: scheduled = [] } = useScheduled();
  const { data: channels = [] } = useChannels();
  const { usersById, nameDisplay } = useUsers();
  const meId = useSession().data?.user?.id ?? '';
  const cancel = useCancelScheduled();
  const confirm = useConfirm();

  const labelOf = (item: ScheduledMessage) => {
    const channel = channels.find((c) => c.id === item.channelId);
    if (!channel) return 'csatorna';
    const title = channelTitle(channel, usersById, meId, nameDisplay);
    return channel.kind === 'public' || channel.kind === 'private' ? `#${title}` : title;
  };

  const askCancel = (item: ScheduledMessage) =>
    void confirm({
      title: item.repeat.kind === 'none' ? 'Ütemezett üzenet lemondása' : 'Ismétlődés leállítása',
      body:
        item.repeat.kind === 'none'
          ? 'Az üzenet nem megy el. A szövegét előtte érdemes kimásolni, ha még kell.'
          : 'A további alkalmak nem mennek ki. A már elküldött üzenetek maradnak.',
      confirmLabel: item.repeat.kind === 'none' ? 'Lemondás' : 'Leállítás',
    }).then((ok) => {
      if (ok) cancel.mutate(item.id);
    });

  return (
    <>
      {scheduled.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-muted">Nincs ütemezett üzeneted.</p>
      ) : (
        <ul className="-mx-1 max-h-[calc(var(--app-height,100dvh)*0.60)] space-y-2 overflow-y-auto px-1">
          {scheduled.map((item) => (
            <li
              key={item.id}
              className="rounded-card border border-border-soft bg-surface-raised px-3 py-2.5"
            >
              <div className="flex items-center gap-2 text-2xs text-ink-faint">
                <Link
                  to="/c/$channelId"
                  params={{ channelId: item.channelId }}
                  onClick={onClose}
                  className="font-semibold text-accent hover:underline"
                >
                  {labelOf(item)}
                </Link>
                <span>·</span>
                <span>
                  {item.repeat.kind === 'none' ? '' : 'következő: '}
                  {new Date(item.sendAt).toLocaleString('hu-HU', WHEN)}
                </span>
                <button
                  type="button"
                  onClick={() => askCancel(item)}
                  disabled={cancel.isPending}
                  className="ml-auto rounded-control px-2 py-0.5 text-xs text-ink-muted hover:bg-surface-hover hover:text-danger disabled:opacity-50"
                >
                  {item.repeat.kind === 'none' ? 'Lemondás' : 'Leállítás'}
                </button>
              </div>

              <p className="mt-1 line-clamp-3 break-words text-sm text-ink">{item.text}</p>

              {item.repeat.kind !== 'none' && (
                <p className="mt-1 flex items-center gap-1.5 text-2xs text-ink-muted">
                  <Icon name="ora" size="sm" />
                  {describeRepeat(new Date(item.repeat.anchor ?? item.sendAt), item.repeat)}
                  {item.repeat.until
                    ? `, ${new Date(item.repeat.until).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}-ig`
                    : ''}
                  {item.lastSentAt
                    ? ` · legutóbb: ${new Date(item.lastSentAt).toLocaleString('hu-HU', WHEN)}`
                    : ''}
                </p>
              )}

              {item.lastError && (
                <p role="alert" className="mt-1 text-2xs text-danger">
                  Nem ment el: {item.lastError}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
